import React, { useCallback, useMemo } from "react";

import {
	BooleanInput,
	DateInput,
	FormTab,
	NumberInput,
	ReferenceInput,
	SelectInput,
	TabbedForm,
	TextInput,
	required,
	minValue,
} from "react-admin";
import RichTextInput from "../../fields/RichTextInput";
import { SelectCampaignStatusInput } from "../../fields/SelectCampaignStatus";
import { UploadOrUrlImageInput } from "../../fields/UploadOrUrlImage";

function UUID() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c == "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export const transform = (formValues) => {
	const values = {
		...formValues,
		// Non-nullable values to create a campaign
		title: formValues.title || "",
		content: formValues.content || "",
		goal: formValues.goal || 0,
		status: formValues.status || "CAMPAIGN_STATUS_DRAFT",
		start_date: formValues.start_date || new Date().toISOString(),
		end_date: formValues.end_date || new Date().toISOString(),
		origin_code: formValues.origin_code || "",
		abstract_title: formValues.abstract_title || "",
		abstract_content: formValues.abstract_content || "",
		abstract_image__url__: formValues.abstract_image__url__ || "https://via.placeholder.com/1000x667",
		meta_title: formValues.meta_title || "",
		url: formValues.url || `/toutes-les-campagnes/${UUID()}`,
		background_image__url__: formValues.background_image__url__ || "https://via.placeholder.com/1000x667",
		campaign_creator_image__url__:
			formValues.campaign_creator_image__url__ || "https://via.placeholder.com/1000x667",
		campaign_creator_fullname: formValues.campaign_creator_fullname || "",
		region_id: formValues.region_id || 8,
	};

	return values;
};

const CampaignForm = (props) => {
	/**
	 * Get the step number.
	 * /#/campaign/create -> 0
	 * /#/campaign/create/1 -> 1
	 * /#/campaign/create/2 -> 2
	 * /#/campaign/create/n -> n
	 */
	const activeStepNumber = useMemo(() => {
		const splitted = window.location.hash.split("/");

		return splitted[3] || "0";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [window.location.hash]);

	const getTitleValidation = useCallback(
		(step) => {
			const map = {
				// Step 0 -> Title is required only on step 0
				0: [required(), false, false, false],
				// Step 1 -> Title is required only on step 1
				1: [false, required(), false, false],
				// Step 2 -> Title is required step 1 and 2
				2: [required(), required(), false, false],
				// Step 3 -> Title is required step 1 and 2
				3: [required(), required(), false, false],
				// Step 4 -> Title is required step 1 and 2
				4: [required(), required(), false, false],
			};

			return map[activeStepNumber][step];
		},
		[activeStepNumber]
	);

	return (
		<TabbedForm {...props} warnWhenUnsavedChanges>
			<FormTab label="Résumé pour liste">
				<TextInput
					fullWidth
					multiline
					source="abstract_title"
					label="Titre"
					validate={getTitleValidation(0)}
				/>
				<UploadOrUrlImageInput fullWidth source="abstract_image" label="Image" />
				<TextInput multiline fullWidth source="abstract_content" label="Texte" />
			</FormTab>

			<FormTab label="Page campagne">
				<TextInput fullWidth source="title" label="Titre" validate={getTitleValidation(1)} />
				<ReferenceInput source="region_id" reference="region">
					<SelectInput label="Region" optionText="value" />
				</ReferenceInput>
				<UploadOrUrlImageInput fullWidth source="background_image" label="Image header" />
				<TextInput fullWidth source="copyright" label="Copyright" />
				<RichTextInput source="content" label="Contenu" />
			</FormTab>

			<FormTab label="Créateur">
				<TextInput source="campaign_creator_fullname" label="Prénom et nom" />
				<TextInput source="campaign_creator_entity_1" label="Fonction (ligne 1)" />
				<TextInput source="campaign_creator_entity_2" label="Fonction (ligne 2)" />
				<UploadOrUrlImageInput fullWidth source="campaign_creator_image" label="Portrait" />
			</FormTab>

			<FormTab label="Configuration">
				<TextInput fullWidth source="url" label="URL de la page" />
				<TextInput fullWidth source="meta_title" label="Titre pour le SEO" />
				<BooleanInput source="is_pinned" label="A la une" defaultValue={false} />
				<NumberInput source="goal" label="Objectif financier" validate={minValue(0)} min={0} />
				<TextInput source="origin_code" label="Code origine" />
				<TextInput source="affectation_code" label="Code affectation" />
				<BooleanInput source="is_doubled" label="Don doublé" defaultValue={false} />
				<SelectCampaignStatusInput
					source="status"
					label="Statut"
					initalValue={"CAMPAIGN_STATUS_DRAFT"}
				/>
				<DateInput source="start_date" label="Date de début" />
				<DateInput source="end_date" label="Date de fin" />
			</FormTab>

			<FormTab label="Partages sociaux">
				<TextInput source="facebook_og_title" label="Titre Facebook" />
				<TextInput source="facebook_og_description" label="Description Facebook" />
				<UploadOrUrlImageInput fullWidth source="facebook_og_image" label="Image Facebook" />
				<TextInput source="twitter_content" label="Contenu Twitter" />
				<TextInput source="twitter_hashtags" label="Hashtags Twitter" />
				<TextInput source="twitter_via" label="Via Twitter" />
				<TextInput source="whatsapp_content" label="Contenu Whatsapp" />
			</FormTab>
		</TabbedForm>
	);
};

export default CampaignForm;
